import { NextPageWithLayout } from '@/pages/_app';
import { GetStaticProps, GetStaticPropsContext, InferGetServerSidePropsType } from 'next';
import { getServerSideProps } from 'next/dist/build/templates/pages';
import React, { useState } from 'react';
import { CustomHead, Layout } from '@/components/Commons';
import { AIKA_LOGO_SQUARE } from '@/shared/constants';
import { productSans } from '@/constants/fonts';
import Image from 'next/image';

const BlogTechCrunchDetailsPage: NextPageWithLayout<InferGetServerSidePropsType<typeof getServerSideProps>> = () => {
  const [lang, setLang] = useState<'en' | 'ru'>('en');
  return (
    <section className={`text-white ${productSans.className} mb-32`}>
      <div className="mx-auto mt-40 max-w-5xl text-xl max-md:mt-28 max-md:w-full max-md:px-4">
        <div className="prose lg:prose-xl dark:prose-invert lg:prose-h1:text-4xl mx-auto mb-10 break-words lg:mt-20">
          <div className="mb-6 text-4xl max-md:text-2xl">
            {lang === 'en' ? (
              <h1>AIKA Takes the Stage at TechCrunch Disrupt 2025</h1>
            ) : (
              <h1>AIKA на сцене TechCrunch Disrupt 2025</h1>
            )}
          </div>
          <div className="mb-16 flex w-full items-center justify-between max-md:mb-10">
            <div className="flex flex-1 gap-2 text-sm italic">
              <span>TechCrunch</span>
              <span>20 Jan 2026</span>
            </div>
            <div className="text-right">
              <span className="group flex w-[60px] cursor-pointer overflow-hidden rounded-md">
                <span
                  className={`w-[30px] items-center justify-center bg-gray-200 pt-1 text-center text-sm text-black ${lang === 'en' ? 'bg-white' : ''}`}
                  onClick={() => setLang('en')}
                >
                  🇺🇸
                </span>
                <span
                  className={`w-[30px] items-center justify-center bg-gray-200 pt-1 text-center text-sm text-black ${lang === 'ru' ? 'bg-white' : ''}`}
                  onClick={() => setLang('ru')}
                >
                  🇷🇺
                </span>
              </span>
            </div>
          </div>
          <div className="relative mb-4 aspect-video w-full overflow-hidden rounded-2xl">
            <Image
              style={{ objectFit: 'cover' }}
              src="/images/blogs/tech-crunch/05436b813efd3faf12149d3217674c5f.jpg"
              fill
              alt="e"
            />
          </div>
          <p className="mb-4 text-base font-extralight">
            {lang === 'en' ? (
              <span>
                This October, the AIKA team traveled to San Francisco to participate in TechCrunch Disrupt 2025 — one of
                the world's most prestigious technology conferences, bringing together leading startups, investors, and
                visionaries shaping the future of technology.
              </span>
            ) : (
              <span>
                В октябре этого года команда AIKA отправилась в Сан-Франциско для участия в TechCrunch Disrupt 2025 —
                одной из самых престижных технологических конференций мира, объединяющей ведущие стартапы, инвесторов и
                визионеров, формирующих будущее технологий.
              </span>
            )}
          </p>
          {lang === 'en' ? (
            <h2 className="mb-4">Silk Road Innovation Hub Pavilion</h2>
          ) : (
            <h2 className="mb-4">Павильон Silk Road Innovation Hub</h2>
          )}
          {lang === 'en' ? (
            <p className="mb-4 text-base font-extralight">
              AIKA was proud to represent Kazakhstan's growing tech ecosystem as part of the Silk Road Innovation Hub
              pavilion. The pavilion united startups from across Central Eurasia, each bringing unique perspectives on
              solving global technology challenges.
            </p>
          ) : (
            <p className="mb-4 text-base font-extralight">
              AIKA с гордостью представляла растущую технологическую экосистему Казахстана в составе павильона Silk Road
              Innovation Hub. Павильон объединил стартапы со всей Центральной Евразии, каждый из которых предлагал
              уникальный подход к решению глобальных технологических задач.
            </p>
          )}
          {lang === 'en' ? (
            <p className="mb-4 text-base font-extralight">
              Our participation demonstrated that innovation knows no borders. While many associate cutting-edge AI
              development exclusively with Silicon Valley, the Silk Road Innovation Hub showcased how entrepreneurs from
              Central Asia are building solutions that resonate with global audiences.
            </p>
          ) : (
            <p className="mb-4 text-base font-extralight">
              Наше участие продемонстрировало, что инновации не знают границ. В то время как многие связывают передовые
              разработки в области ИИ исключительно с Кремниевой долиной, Silk Road Innovation Hub показал, как
              предприниматели из Центральной Азии создают решения, находящие отклик у глобальной аудитории.
            </p>
          )}
          {lang === 'en' ? (
            <h2 className="mb-4">Intelligence Augmentation: Our Vision</h2>
          ) : (
            <h2 className="mb-4">Intelligence Augmentation: наше видение</h2>
          )}
          {lang === 'en' ? (
            <p className="mb-4 text-base font-extralight">
              Throughout the conference, we presented AIKA's Intelligence Augmentation platform to investors, founders,
              and technology leaders who share our conviction: the future of AI is not about replacing human
              intelligence, but enhancing it.
            </p>
          ) : (
            <p className="mb-4 text-base font-extralight">
              На протяжении всей конференции мы представляли платформу Intelligence Augmentation от AIKA инвесторам,
              основателям компаний и технологическим лидерам, разделяющим нашу убеждённость: будущее ИИ не в замене
              человеческого интеллекта, а в его усилении.
            </p>
          )}
          {lang === 'en' ? (
            <p className="mb-4 text-base font-extralight">
              The conversations we had at Disrupt confirmed that we are asking the right questions about human-AI
              interaction. Our approach — building meaningful connections between humans and AI that help users discover
              new perspectives and learn about themselves — resonated more than we had anticipated.
            </p>
          ) : (
            <p className="mb-4 text-base font-extralight">
              Разговоры на Disrupt подтвердили, что мы задаём правильные вопросы о взаимодействии человека и ИИ. Наш
              подход — создание значимых связей между людьми и ИИ, помогающих пользователям открывать новые перспективы
              и познавать себя — нашёл больший отклик, чем мы ожидали.
            </p>
          )}
          <div className="mb-4 flex gap-4 max-md:flex-col">
            <div className="relative aspect-[0.75] w-1/2 overflow-hidden rounded-2xl max-md:w-full">
              <Image
                style={{ objectFit: 'cover' }}
                src="/images/blogs/tech-crunch/2d97421247076229689d73f9b0c610e4.jpg"
                fill
                alt="e"
              />
            </div>
            <div className="flex-1/2">
              {lang === 'en' ? (
                <h2 className="mb-4">Pitching on the Main Stage</h2>
              ) : (
                <h2 className="mb-4">Питч на главной сцене</h2>
              )}
              {lang === 'en' ? (
                <p className="mb-4 text-base font-extralight">
                  A highlight of our participation was the opportunity to pitch AIKA on the Silk Road Innovation Hub
                  main stage at TechCrunch Disrupt. In four minutes, we articulated why Intelligence Augmentation
                  matters and how our platform creates meaningful human-AI interaction.
                </p>
              ) : (
                <p className="mb-4 text-base font-extralight">
                  Ключевым моментом нашего участия стала возможность представить AIKA на главной сцене Silk Road
                  Innovation Hub в рамках TechCrunch Disrupt. За четыре минуты мы объяснили, почему Intelligence
                  Augmentation важен и как наша платформа создаёт значимое взаимодействие человека с ИИ.
                </p>
              )}
              {lang === 'en' ? (
                <p className="mb-4 text-base font-extralight">
                  Standing on that stage, representing Kazakhstan's technology sector on a global platform, felt
                  significant. It reinforced our belief that good ideas deserve global visibility, and initiatives like
                  Silk Road Innovation Hub create exactly those opportunities.
                </p>
              ) : (
                <p className="mb-4 text-base font-extralight">
                  Стоя на той сцене и представляя технологический сектор Казахстана на глобальной платформе, мы ощущали
                  значимость момента. Это укрепило нашу веру в то, что хорошие идеи заслуживают глобальной видимости, и
                  такие инициативы, как Silk Road Innovation Hub, создают именно такие возможности.
                </p>
              )}
              {lang === 'en' ? <h2 className="mb-4">Key Takeaways</h2> : <h2 className="mb-4">Ключевые выводы</h2>}
              {lang === 'en' ? (
                <p className="mb-4 text-base font-extralight">
                  Our experience at TechCrunch Disrupt 2025 affirmed several important insights:
                </p>
              ) : (
                <p className="mb-4 text-base font-extralight">
                  Наш опыт на TechCrunch Disrupt 2025 подтвердил несколько важных наблюдений.
                </p>
              )}
              {lang === 'en' ? (
                <p className="mb-4 text-base font-extralight">
                  The global technology community is increasingly receptive to perspectives that originate outside
                  traditional tech hubs. Our approach to Intelligence Augmentation — developed from our own context and
                  understanding of what people actually need from AI — offers something distinctive in a market often
                  dominated by similar solutions.
                </p>
              ) : (
                <p className="mb-4 text-base font-extralight">
                  Глобальное технологическое сообщество всё более открыто к перспективам, возникающим за пределами
                  традиционных технологических центров. Наш подход к Intelligence Augmentation — разработанный исходя из
                  нашего собственного контекста и понимания того, что людям действительно нужно от ИИ — предлагает нечто
                  особенное на рынке, где часто доминируют похожие решения.
                </p>
              )}
              {lang === 'en' ? (
                <p className="mb-4 text-base font-extralight">
                  The future of AI lies in augmenting human capabilities, not replacing them. This philosophy continues
                  to guide everything we build at AIKA.
                </p>
              ) : (
                <p className="mb-4 text-base font-extralight">
                  Будущее ИИ — в усилении человеческих возможностей, а не в их замене. Эта философия продолжает
                  направлять всё, что мы создаём в AIKA.
                </p>
              )}
              {lang === 'en' ? (
                <p className="mb-4 text-base font-extralight">
                  We returned from San Francisco energized and focused on our mission: building AI that genuinely
                  enhances human potential.
                </p>
              ) : (
                <p className="mb-4 text-base font-extralight">
                  Мы вернулись из Сан-Франциско с новой энергией и сфокусированностью на нашей миссии: создание ИИ,
                  который по-настоящему раскрывает человеческий потенциал.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

BlogTechCrunchDetailsPage.getLayout = (page: React.ReactElement) => {
  return (
    <>
      <CustomHead
        title="AIKA Takes the Stage at TechCrunch Disrupt 2025"
        description="This October, the AIKA team traveled to San Francisco to participate in TechCrunch Disrupt 2025 — one of the world's most prestigious technology conferences, bringing together leading startups, investors, and visionaries shaping the future of technology."
        image={AIKA_LOGO_SQUARE}
      />
      <Layout hideHeader={false} stickyHeader={false}>
        {page}
      </Layout>
    </>
  );
};
export const getStaticProps = (async ({ locale }: GetStaticPropsContext) => {
  return {
    props: {
      messages: (await import(`@/locales/${locale}.json`)).default,
    },
  };
}) satisfies GetStaticProps;

export default BlogTechCrunchDetailsPage;
